import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import {
  Box,
  ClickAwayListener,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  Tooltip as MUITooltip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Add, DeleteOutline } from '@mui/icons-material';

import { CalendarDateService } from '@uplift/services';
import { WeekDayKeys } from '@uplift/types';
import { Button, TimeRangeInput, TimeRangeInputValue, Tooltip } from '@uplift/ui-v2';
import { CopyAllOutlined } from '@uplift/icons';
import { useL10n } from '@uplift/hooks';

import { CopyAvailabilityForm } from './CopyAvailabilityForm';


const useStyles = makeStyles()(theme => ({
  item: {
    marginBottom: theme.spacing(1),

    '&:last-child': {
      marginBottom: 0,
    }
  },
  tooltip: {
    padding: 0,
  }
}));


export const AvailabilityFormRow: React.FC<{ title: string; namePrefix: WeekDayKeys }> = React.memo(({ title, namePrefix }) => {
  type FormikValues = Record<WeekDayKeys, TimeRangeInputValue[]>;
  const l10n = useL10n();
  const parentFormikContext = useFormikContext<FormikValues>();
  const valuesAvailability = parentFormikContext.values[namePrefix];
  const { classes } = useStyles();

  const [ copyTooltipOpen, setCopyTooltipOpen ] = React.useState(false);

  return (
    <TableRow>
      <TableCell style={{ paddingLeft: 0, paddingRight: 0, width: '84px', verticalAlign: 'top' }}>
        <Box height="50px" display="flex" alignItems="center">
          <Typography variant="body1">
            {title}
          </Typography>
        </Box>
      </TableCell>
      <TableCell style={{ paddingLeft: 0 }}>
        <FieldArray
          name={namePrefix}
          render={arrayHelpers => {
            if (valuesAvailability.length) {
              return valuesAvailability.map((a, index, arr) => (
                <Box key={a.id} className={classes.item}>
                  <TimeRangeInput
                    fullWidth
                    lessThenHourWarningTitle={l10n.calendar.availability.editForm.lessThenHourWarningTitle}
                    warningOnLessThenHour={true}
                    name={`${namePrefix}.${index}`}
                    inlineWarning
                    append={(
                      <Box width="144px">
                        <MUITooltip title={l10n.calendar.availability.editForm.buttons.remove}>
                          <IconButton onClick={() => arrayHelpers.remove(index)}><DeleteOutline /></IconButton>
                        </MUITooltip>
                        {index === 0 && (
                          <Tooltip
                            classes={{ tooltip: classes.tooltip }}
                            arrow
                            light
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            open={copyTooltipOpen}
                            title={(
                              <ClickAwayListener onClickAway={() => setCopyTooltipOpen(false)}>
                                <Box minWidth="162px">
                                  <CopyAvailabilityForm
                                    weekDay={namePrefix}
                                    onClose={() => setCopyTooltipOpen(false)}
                                    onSubmit={values => {
                                      const newValues = Object.keys(values).filter(k => values[k]).reduce<Partial<Record<WeekDayKeys, TimeRangeInputValue[]>>>((prev, curr) => ({
                                        ...prev,
                                        [curr]: valuesAvailability.map(v => ({ ...v })),
                                      }), {});


                                      parentFormikContext.setValues({
                                        ...parentFormikContext.values,
                                        ...newValues,
                                      });
                                    }}
                                  />
                                </Box>
                              </ClickAwayListener>
                            )}
                            placement="right-start"
                          >
                            <MUITooltip title={l10n.calendar.availability.editForm.buttons.copyToOtherDays}>
                              <IconButton
                                onClick={() => {
                                  setCopyTooltipOpen(true);
                                }}
                              >
                                <CopyAllOutlined />
                              </IconButton>
                            </MUITooltip>
                          </Tooltip>
                        )}
                        {index + 1 >= arr.length && (
                          <MUITooltip title={l10n.calendar.availability.editForm.buttons.add}>
                            <IconButton
                              onClick={() => {
                                const lastAvailability = valuesAvailability[valuesAvailability.length - 1];
                                const lastAvailabilityTime = CalendarDateService.time(lastAvailability.end);

                                arrayHelpers.push({
                                  id: Date.now(),
                                  start: lastAvailabilityTime.add(1, 'hour').format('HH:mm:ss'),
                                  end: null,
                                });
                              }}
                            >
                              <Add />
                            </IconButton>
                          </MUITooltip>
                        )}
                      </Box>
                    )}
                  />
                </Box>
              ));
            }

            return (
              <Box ml={3}>
                <Button
                  startIcon={<Add />}
                  color="secondary"
                  onClick={() => {
                    arrayHelpers.push({ id: Date.now(), start: '09:00:00', end: '17:00:00' });
                  }}
                >
                  Add hours
                </Button>
              </Box>
            );
          }}
        />
      </TableCell>
    </TableRow>
  );
});
